import React from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Box, Flex, HStack, Icon, Image, Menu, Portal, Stack, Text } from "@chakra-ui/react";
import { MdCalendarToday, MdMoreHoriz, MdPeople, MdPerson, MdSupervisorAccount } from "react-icons/md";

const metricItems = [
  { key: "socios", icon: MdPeople, label: "Socios" },
  { key: "instructores", icon: MdPerson, label: "Instructores" },
  { key: "preceptorias", icon: MdSupervisorAccount, label: "Preceptorias" },
];

function MetricPill({ icon, value, label }) {
  return (
    <HStack
      gap="1.5"
      px="2.5"
      py="1.5"
      rounded="full"
      bg="dashboard.bg.surfaceMuted"
      color="dashboard.fg.muted"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      minW="0"
      title={label}
    >
      <Icon as={icon} boxSize="3.5" flexShrink={0} color="dashboard.fg.subtle" />
      <Text fontSize="xs" fontWeight="800" lineHeight="1">
        {value ?? 0}
      </Text>
    </HStack>
  );
}

function CardActs({ id, nombre, fecha, foto, socios, instructores, preceptorias, published = true, isIncomplete = false, onDelete, onTogglePublish }) {
  const navigate = useNavigate();
  const menuItemProps = { _highlighted: { bg: "dashboard.bg.surfaceMuted" } };
  const metrics = { socios, instructores, preceptorias };

  const goEdit = () => navigate(`/dashboard/club/actividades/${id}`);
  const doDelete = () => { if (typeof onDelete === "function") onDelete(id); };
  const doTogglePublish = () => { if (typeof onTogglePublish === "function") onTogglePublish(id); };

  return (
    <Menu.Root>
      <Box
        w="100%"
        h="100%"
        overflow="hidden"
        bg="dashboard.bg.surface"
        borderWidth="1px"
        borderColor="dashboard.border.muted"
        rounded="card"
        cursor="pointer"
        role="group"
        tabIndex={0}
        boxShadow="0 1px 0 rgba(0,0,0,0.03)"
        transition="transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease"
        _hover={{ transform: "translateY(-3px)", boxShadow: "cardLift", borderColor: "dashboard.border.default" }}
        onClick={goEdit}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            goEdit();
          }
        }}
      >
        {/* Image */}
        <Box position="relative" overflow="hidden" aspectRatio="4 / 3" bg="dashboard.bg.surfaceMuted">
          <Image
            src={foto}
            alt={nombre}
            objectFit="cover"
            h="100%"
            w="100%"
            transition="transform 500ms ease"
            filter={published ? "none" : "grayscale(1) opacity(0.65)"}
            _groupHover={{ transform: "scale(1.04)" }}
          />
          <Box
            position="absolute"
            inset="0"
            bg="linear-gradient(to top, rgba(0,0,0,0.56), rgba(0,0,0,0.08) 58%, transparent)"
          />
          <HStack
            position="absolute"
            top="2.5"
            left="2.5"
            right="2.5"
            justify="space-between"
            align="start"
            gap="2"
          >
            <HStack gap="1.5" flexShrink={0}>
              {!published && (
                <Badge
                  colorPalette="gray"
                  variant="solid"
                  rounded="full"
                  px="2.5"
                  py="1"
                  fontSize="10px"
                  fontWeight="800"
                  letterSpacing="0.04em"
                >
                  Borrador
                </Badge>
              )}
              {isIncomplete && (
                <Badge
                  colorPalette="red"
                  variant="solid"
                  rounded="full"
                  px="2.5"
                  py="1"
                  fontSize="10px"
                  fontWeight="800"
                  letterSpacing="0.04em"
                >
                  Incompleto
                </Badge>
              )}
            </HStack>
            <Menu.Trigger asChild>
              <Box
                as="button"
                type="button"
                display="grid"
                placeItems="center"
                boxSize="7"
                rounded="full"
                bg="blackAlpha.500"
                color="white"
                cursor="pointer"
                onClick={(event) => event.stopPropagation()}
                aria-label="Opciones de actividad"
                _hover={{ bg: "blackAlpha.700" }}
              >
                <MdMoreHoriz />
              </Box>
            </Menu.Trigger>
          </HStack>
        </Box>

        {/* Body */}
        <Stack p="3.5" gap="3">
          <Text
            fontFamily="display"
            fontWeight="700"
            fontSize={{ base: "sm", md: "md" }}
            color="dashboard.fg.default"
            lineClamp={2}
            lineHeight="1.15"
            minH={{ base: "32px", md: "36px" }}
          >
            {nombre}
          </Text>
          <HStack gap="1.5" color="dashboard.fg.muted" minW="0">
            <Icon as={MdCalendarToday} boxSize="3.5" flexShrink={0} />
            <Text fontSize="xs" fontWeight="700" lineClamp={1}>
              {fecha || "Fecha por definir"}
            </Text>
          </HStack>
          <Flex gap="2" wrap="wrap">
            {metricItems.map((item) => (
              <MetricPill key={item.key} icon={item.icon} value={metrics[item.key]} label={item.label} />
            ))}
          </Flex>
        </Stack>
      </Box>

      <Portal>
        <Menu.Positioner>
          <Menu.Content bg="dashboard.bg.popover" color="dashboard.fg.default" borderColor="dashboard.border.default">
            <Menu.Item value="edit" onClick={goEdit} {...menuItemProps}>Editar</Menu.Item>
            <Menu.Item value={published ? "unpublish" : "publish"} onClick={doTogglePublish} {...menuItemProps}>
              {published ? "Despublicar" : "Publicar"}
            </Menu.Item>
            <Menu.Separator />
            <Menu.Item value="delete" onClick={doDelete} color="dashboard.feedback.error" {...menuItemProps}>Eliminar</Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

export default CardActs;
